import React, { useState, useEffect, useMemo, useCallback } from 'react';
import SearchWidgetConfigModal from './SearchWidgetConfigModal';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Card,
  Badge,
  Alert,
  Dropdown,
  ListGroup,
  Accordion,
  Table,
  InputGroup,
  ButtonGroup
} from 'react-bootstrap';
import {
  FaPlus,
  FaTrash,
  FaEdit,
  FaEye,
  FaSave,
  FaCog,
  FaList,
  FaLayerGroup,
  FaArrowRight,
  FaInfoCircle,
  FaCheck,
  FaTimes,
  FaGripVertical,
  FaSort,
  FaFilter,
  FaSearch
} from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DataApi from '../../api/DataApi';
import './css/RelatedListManager.css';

const DEFAULT_RELATED_LIST_ACTIONS = ['new', 'edit', 'delete', 'bulk_delete', 'import', 'export'];
const DEFAULT_STANDARD_FIELDS = [
  'name',
  'ownerid',
  'createdbyid',
  'lastmodifiedbyid',
  'createddate',
  'lastmodifieddate'
];

const normalizeActions = (actions, { fallbackToDefault = false } = {}) => {
  if (Array.isArray(actions)) {
    const normalized = actions
      .map(action => String(action).toLowerCase())
      .filter(action => action && DEFAULT_RELATED_LIST_ACTIONS.includes(action));

    return Array.from(new Set(normalized));
  }

  return fallbackToDefault ? [...DEFAULT_RELATED_LIST_ACTIONS] : [];
};

const formatActionLabel = (action) => {
  if (!action) return '';

  return action
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const humanizeObjectApiName = (name) => {
  if (!name || typeof name !== 'string') return '';
  return name
    .replace(/__/g, ' ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Caption for a related list. If several lists share the same target object, uses
 * "Object (Lookup field)"; otherwise uses the normal label (relationship_label, else object).
 * @param {Map<string, number>} [targetCounts] - target_object_name → count (available + on layout)
 */
const relatedListKindCaption = (rl, targetCounts) => {
  if (!rl) return '';
  const isActivityTimeline =
    rl.activity_timeline === true ||
    rl.relationship_label === 'Activity Timeline' ||
    (rl.target_object_name === 'activity' &&
      rl.source_field_name === 'id' &&
      rl.target_field_name === 'related_record_id');
  if (isActivityTimeline) {
    return rl.relationship_label || 'Activity Timeline';
  }
  const target = rl.target_object_name;
  let sameTargetCount = 1;
  if (target && targetCounts instanceof Map) {
    sameTargetCount = targetCounts.get(target) || 0;
  }
  const objFromApi = rl.target_object_label != null && String(rl.target_object_label).trim();
  const objectLabel = objFromApi || humanizeObjectApiName(rl.target_object_name);
  const relLabel = rl.relationship_label && String(rl.relationship_label).trim();

  if (!target || sameTargetCount <= 1) {
    return relLabel || objectLabel || '';
  }

  const tf = rl.target_field_name != null ? String(rl.target_field_name).trim() : '';
  const skipFieldPart = !tf || tf.toLowerCase() === 'id';
  let fieldLabel =
    (rl.target_lookup_field_label != null && String(rl.target_lookup_field_label).trim()) ||
    (rl.object_fields || []).find((f) => f.field_name === tf)?.field_label ||
    '';
  fieldLabel = fieldLabel ? String(fieldLabel).trim() : '';
  if (skipFieldPart || !fieldLabel) {
    return relLabel || objectLabel || '';
  }
  return `${objectLabel} (${fieldLabel})`;
};

/** Child object column used to match the parent record (may be pipe-separated for OR match). */
const relatedListTargetFieldApi = (rl) => {
  if (!rl || rl.target_field_name == null) return null;
  const s = String(rl.target_field_name).trim();
  return s || null;
};

/** Child lookup columns → User; hidden from related-list picker as noisy. */
const HIDDEN_RELATED_LIST_TARGET_FIELDS = new Set([
  'createdbyid',
  'lastmodifiedbyid',
  'ownerid',
]);

const isHiddenSystemUserRelationship = (rl) => {
  const raw = relatedListTargetFieldApi(rl);
  if (!raw) return false;
  const parts = raw
    .split('|')
    .map((p) => String(p).trim().toLowerCase())
    .filter(Boolean);
  return parts.some((p) => HIDDEN_RELATED_LIST_TARGET_FIELDS.has(p));
};

const arraysEqual = (a = [], b = []) => {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

const isFieldReadonly = (field = {}) => {
  if (field == null) return false;
  const readonlyFlags = [
    field.is_readonly,
    field.isReadonly,
    field.readonly
  ];
  return readonlyFlags.find(value => value !== undefined) === true;
};

const isFieldRequired = (field = {}) => {
  if (field == null) return false;
  const requiredFlags = [
    field.is_required,
    field.isRequired,
    field.required
  ];
  return requiredFlags.find(value => value !== undefined) === true;
};

const getBaseRequiredFieldNames = (objectFields = []) => {
  return (objectFields || [])
    .filter(field => isFieldRequired(field))
    .map(field => field.field_name)
    .filter(fieldName => {
      if (!fieldName) return false;
      return fieldName.toLowerCase() !== 'ownerid';
    });
};

const getDefaultStandardFieldNames = (objectFields = []) => {
  const fieldMap = new Map(
    (objectFields || [])
      .filter(field => field?.field_name)
      .map(field => [String(field.field_name).toLowerCase(), field.field_name])
  );

  return DEFAULT_STANDARD_FIELDS
    .filter(fieldName => fieldMap.has(fieldName))
    .map(fieldName => fieldMap.get(fieldName));
};

const RelatedListManager = ({ objectName, layoutId, onClose, onSave }) => {
  const showBulkFieldsConfig = false;
  const [relatedLists, setRelatedLists] = useState([]);
  const [layoutRelatedLists, setLayoutRelatedLists] = useState([]);
  const [searchWidgets, setSearchWidgets] = useState([]);
  const [layoutMetadataForWidgets, setLayoutMetadataForWidgets] = useState({
    total_columns: 3,
    detail_column_position: 1
  });
  const [showSearchWidgetModal, setShowSearchWidgetModal] = useState(false);
  const [editingSearchWidget, setEditingSearchWidget] = useState(null);
  const [availableFields, setAvailableFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedRelatedList, setSelectedRelatedList] = useState(null);
  const [configForm, setConfigForm] = useState({
    fields: [],
    actions: normalizeActions(null, { fallbackToDefault: true }),
    max_records: 50,
    display_order: 0,
    is_active: true,
    relationship_label: '',
    field_config: {
      fields: [],
      sortBy: "createddate",
      sortOrder: "DESC",
      required_fields: [],
      bulk_action_fields: [],
      bulk_action_required_fields: []
    },
    bulk_action_field_config: null
  });
  const [fieldConfigMode, setFieldConfigMode] = useState('list');
  const [fieldSearchTerm, setFieldSearchTerm] = useState('');
  const [saveBulkFields, setSaveBulkFields] = useState(false);

  // Auto-dismiss success messages after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess('');
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (objectName) {
      fetchRelatedLists();
      fetchLayoutRelatedLists();
    }
  }, [objectName, layoutId]);

  useEffect(() => {
    if (layoutId) {
      fetchLayoutSearchWidgets();
    } else {
      setSearchWidgets([]);
    }
  }, [layoutId]);

  const fetchLayoutSearchWidgets = async () => {
    try {
      if (!layoutId) return;
      const api = new DataApi(`layoutwidget/layout/${layoutId}`);
      const response = await api.fetchAll();
      if (response?.data?.success) {
        let widgetsArray = [];
        if (Array.isArray(response.data.data)) {
          widgetsArray = response.data.data;
        } else if (response.data.data?.widgets && Array.isArray(response.data.data.widgets)) {
          widgetsArray = response.data.data.widgets;
        }
        const parsed = widgetsArray.map((w) => {
          let cfg = w.config;
          if (typeof cfg === 'string') {
            try {
              cfg = JSON.parse(cfg);
            } catch {
              cfg = {};
            }
          }
          return { ...w, config: cfg || {} };
        });
        const searchOnly = parsed.filter((w) => {
          if (w.widget_type !== 'search') return false;
          const cfg = w.config || {};
          return cfg.show_in_detail_tabs !== false;
        });
        setSearchWidgets(searchOnly);
        const meta = response.data.layout_metadata || response.data.data?.layout_metadata;
        if (meta) {
          setLayoutMetadataForWidgets({
            total_columns: meta.total_columns || 3,
            detail_column_position: meta.detail_column_position || 1
          });
        }
      } else {
        setSearchWidgets([]);
      }
    } catch (e) {
      console.error('[RelatedListManager] fetchLayoutSearchWidgets:', e);
      setSearchWidgets([]);
    }
  };

  const mergedLayoutPanel = useMemo(() => {
    const rl = (layoutRelatedLists || []).map((c) => ({
      kind: 'rl',
      id: c.id,
      order: Number(c.display_order) || 0,
      rl: c
    }));
    const sw = (searchWidgets || []).map((w) => ({
      kind: 'search',
      id: w.id,
      order: Number(w.display_order) || 0,
      widget: w
    }));
    const all = [...rl, ...sw];
    all.sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      if (a.kind !== b.kind) return a.kind === 'rl' ? -1 : 1;
      return String(a.id).localeCompare(String(b.id));
    });
    return all;
  }, [layoutRelatedLists, searchWidgets]);

  /** How many related-list rows (pool + layout) point at each target_object_name — drives lookup disambiguation. */
  const relatedListTargetCounts = useMemo(() => {
    const m = new Map();
    const bump = (name) => {
      if (!name || typeof name !== 'string') return;
      m.set(name, (m.get(name) || 0) + 1);
    };
    (relatedLists || []).forEach((rl) => {
      if (!isHiddenSystemUserRelationship(rl)) bump(rl.target_object_name);
    });
    (layoutRelatedLists || []).forEach((c) => bump(c.target_object_name));
    return m;
  }, [relatedLists, layoutRelatedLists]);

  useEffect(() => {
    setFieldSearchTerm('');
  }, [fieldConfigMode, selectedRelatedList]);

  useEffect(() => {
    if (!showBulkFieldsConfig && fieldConfigMode === 'bulk') {
      setFieldConfigMode('list');
    }
  }, [fieldConfigMode, showBulkFieldsConfig]);

  const fetchRelatedLists = async () => {
    try {
      setLoading(true);
      setError('');

      const api = new DataApi(`relatedlist/${objectName}`);
      const response = await api.fetchAll();

      if (response?.data?.success) {
        console.log('Fetched related lists:', response.data.data);
        setRelatedLists(response.data.data || []);
      } else {
        setRelatedLists([]);
      }
    } catch (error) {
      console.error('Error fetching related lists:', error);
      setError('Failed to load related lists: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchLayoutRelatedLists = async () => {
    try {
      if (!layoutId) return;

      const api = new DataApi(`relatedlist/layout/${layoutId}`);
      const response = await api.fetchAll();

      if (response?.data?.success) {
        const normalizedLayouts = (response.data.data || []).map(item => ({
          ...item,
          actions: normalizeActions(item.actions, { fallbackToDefault: true })
        }));
        setLayoutRelatedLists(normalizedLayouts);
      } else {
        setLayoutRelatedLists([]);
      }
    } catch (error) {
      console.error('Error fetching layout related lists:', error);
    }
  };

  const handleAddActivityTimeline = async () => {
    try {
      setLoading(true);
      setError('');

      // Step 1: Create or get Activity Timeline related list config
      const activityApi = new DataApi('relatedlist/create-activity-timeline');
      const activityResponse = await activityApi.create({
        source_object_name: objectName
      });

      if (!activityResponse?.data?.success) {
        setError('Failed to create Activity Timeline related list: ' + (activityResponse?.data?.message || 'Unknown error'));
        setLoading(false);
        return;
      }

      const activityRelatedList = activityResponse.data.data;

      // Step 2: Add the related list to the layout
      const allFieldNames = activityRelatedList.object_fields
        ? activityRelatedList.object_fields.map(field => field.field_name).filter(Boolean)
        : [];

      const activityListLabel = relatedListKindCaption(activityRelatedList, relatedListTargetCounts);
      const configData = {
        layout_id: layoutId,
        related_list_id: activityRelatedList.id,
        actions: [],
        max_records: 100,
        display_order: layoutRelatedLists.length + searchWidgets.length,
        is_active: true,
        field_config: {
          fields: allFieldNames,
          sortBy: "activity_date",
          sortOrder: "DESC",
          required_fields: [],
          bulk_action_fields: [],
          bulk_action_required_fields: [],
          relationship_label: activityListLabel
        },
        bulk_action_field_config: null
      };

      const api = new DataApi('relatedlist/layout');
      const response = await api.create(configData);

      if (response?.data?.success) {
        setSuccess('Activity Timeline added to layout successfully');
        fetchLayoutRelatedLists();
        fetchRelatedLists();
        fetchLayoutSearchWidgets();
        if (onSave) onSave();
      } else {
        setError('Failed to add Activity Timeline to layout');
      }
    } catch (error) {
      console.error('Error adding Activity Timeline to layout:', error);
      setError('Failed to add Activity Timeline: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAddRelatedList = (relatedList) => {
    const defaultFieldNames = getDefaultStandardFieldNames(relatedList.object_fields);
    const baseRequiredFields = getBaseRequiredFieldNames(relatedList.object_fields);
    const editableBulkFieldNames = relatedList.object_fields
      ? relatedList.object_fields.filter(field => !isFieldReadonly(field)).map(field => field.field_name).filter(Boolean)
      : [];
    const baseBulkRequiredFields = baseRequiredFields.filter(fieldName =>
      editableBulkFieldNames.includes(fieldName)
    );

    setSelectedRelatedList(relatedList);
    setSaveBulkFields(false);
    const defaultListLabel = relatedListKindCaption(relatedList, relatedListTargetCounts);
    setConfigForm({
      actions: normalizeActions(null, { fallbackToDefault: true }),
      max_records: 50,
      display_order: layoutRelatedLists.length + searchWidgets.length,
      is_active: true,
      relationship_label: defaultListLabel,
      field_config: {
        fields: defaultFieldNames,
        sortBy: "createddate",
        sortOrder: "DESC",
        required_fields: baseRequiredFields,
        bulk_action_fields: [],
        bulk_action_required_fields: baseBulkRequiredFields,
        relationship_label: defaultListLabel
      },
      bulk_action_field_config: null
    });
    setFieldConfigMode('list');
    setShowConfigModal(true);
  };

  const handleQuickAddRelatedList = async (relatedList) => {
    try {
      setLoading(true);
      setError('');

      const defaultFieldNames = getDefaultStandardFieldNames(relatedList.object_fields);
      const baseRequiredFields = getBaseRequiredFieldNames(relatedList.object_fields);
      const editableBulkFieldNames = relatedList.object_fields
        ? relatedList.object_fields.filter(field => !isFieldReadonly(field)).map(field => field.field_name).filter(Boolean)
        : [];
      const baseBulkRequiredFields = baseRequiredFields.filter(fieldName =>
        editableBulkFieldNames.includes(fieldName)
      );

      const defaultListLabel = relatedListKindCaption(relatedList, relatedListTargetCounts);
      const configData = {
        layout_id: layoutId,
        related_list_id: relatedList.id,
        actions: normalizeActions(null, { fallbackToDefault: true }),
        max_records: 50,
        display_order: layoutRelatedLists.length + searchWidgets.length,
        is_active: true,
        field_config: {
          fields: defaultFieldNames,
          sortBy: "createddate",
          sortOrder: "DESC",
          required_fields: baseRequiredFields,
          bulk_action_fields: [],
          bulk_action_required_fields: baseBulkRequiredFields,
          relationship_label: defaultListLabel
        }
      };

      const api = new DataApi('relatedlist/layout');
      const response = await api.create(configData);

      if (response?.data?.success) {
        setSuccess('Related list added to layout successfully');
        fetchLayoutRelatedLists();
        fetchLayoutSearchWidgets();
        if (onSave) onSave();
      } else {
        setError('Failed to add related list to layout');
      }
    } catch (error) {
      console.error('Error adding related list to layout:', error);
      setError('Failed to add related list to layout: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleVisualAddRelatedList = async (relatedList) => {
    console.log('Creating config for related list:', relatedList);
    console.log('Related list ID:', relatedList.id);

    // Validate that we have a proper UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (!relatedList.id || !uuidRegex.test(relatedList.id)) {
      console.error('Invalid related list ID:', relatedList.id);
      setError('Invalid related list ID. Please refresh and try again.');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const defaultFieldNames = getDefaultStandardFieldNames(relatedList.object_fields);
      const baseRequiredFields = getBaseRequiredFieldNames(relatedList.object_fields);
      const editableBulkFieldNames = relatedList.object_fields
        ? relatedList.object_fields.filter(field => !isFieldReadonly(field)).map(field => field.field_name).filter(Boolean)
        : [];
      const baseBulkRequiredFields = baseRequiredFields.filter(fieldName =>
        editableBulkFieldNames.includes(fieldName)
      );

      const defaultListLabel = relatedListKindCaption(relatedList, relatedListTargetCounts);
      const configData = {
        layout_id: layoutId,
        related_list_id: relatedList.id,
        actions: normalizeActions(null, { fallbackToDefault: true }),
        max_records: 50,
        display_order: layoutRelatedLists.length + searchWidgets.length,
        is_active: true,
        field_config: {
          fields: defaultFieldNames,
          sortBy: "createddate",
          sortOrder: "DESC",
          required_fields: baseRequiredFields,
          bulk_action_fields: [],
          bulk_action_required_fields: baseBulkRequiredFields,
          relationship_label: defaultListLabel
        }
      };

      const api = new DataApi('relatedlist/layout');
      const response = await api.create(configData);

      if (response?.data?.success) {
        // Add the saved config to the layout list
        const savedConfig = {
          ...response.data.data,
          actions: normalizeActions(response.data.data?.actions, { fallbackToDefault: true }),
          relationship_name: relatedList.relationship_name,
          relationship_label: defaultListLabel,
          target_object_name: relatedList.target_object_name,
          relationship_type: relatedList.relationship_type,
          object_fields: relatedList.object_fields || [],
          target_object_label: relatedList.target_object_label,
          target_lookup_field_label: relatedList.target_lookup_field_label,
          source_field_name: relatedList.source_field_name,
          target_field_name: relatedList.target_field_name
        };

        setLayoutRelatedLists(prev => [...prev, savedConfig]);

        // Remove from available related lists
        setRelatedLists(prev => prev.filter(rl => rl.id !== relatedList.id));

        setSuccess('Related list added to layout successfully');
        fetchLayoutSearchWidgets();
        if (onSave) onSave();
      } else {
        setError('Failed to add related list to layout');
      }
    } catch (error) {
      console.error('Error adding related list to layout:', error);
      setError('Failed to add related list to layout: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setLoading(true);
      setError('');

      // Ensure we're using the correct related_list_id
      const relatedListId = selectedRelatedList.is_temp ? selectedRelatedList.related_list_id : selectedRelatedList.id;

      // Validate that we have a proper related_list_id
      if (!relatedListId || relatedListId.startsWith('temp-')) {
        throw new Error(`Invalid related_list_id: ${relatedListId}. Expected a valid UUID.`);
      }

      const fieldConfig = configForm.field_config || {};
      const bulkFieldConfig = configForm.bulk_action_field_config || {};
      const relatedObjectFields = selectedRelatedList?.object_fields || [];
      const allFieldNamesForSave = relatedObjectFields.map(field => field.field_name);
      const bulkSelectableFieldNamesForSave = relatedObjectFields
        .filter(field => !isFieldReadonly(field))
        .map(field => field.field_name);
      const sanitizedDisplayFields = (fieldConfig.fields || []).filter(fieldName =>
        allFieldNamesForSave.includes(fieldName)
      );
      let sanitizedBulkFields = [];
      const selectedUpsertKey = bulkFieldConfig.upsert_key && bulkUpsertOptions.includes(bulkFieldConfig.upsert_key)
        ? bulkFieldConfig.upsert_key
        : 'id';
      const sanitizedRequiredFields = buildListRequiredFields(
        sanitizedDisplayFields,
        (fieldConfig.required_fields || []).filter(fieldName =>
          sanitizedDisplayFields.includes(fieldName)
        )
      );
      if (saveBulkFields) {
        sanitizedBulkFields = enforceRequiredBulkFields(
          (bulkFieldConfig.fields || []).filter(fieldName =>
            bulkSelectableFieldNamesForSave.includes(fieldName)
          )
        );
        if (
          selectedUpsertKey &&
          bulkSelectableFieldNamesForSave.includes(selectedUpsertKey) &&
          !sanitizedBulkFields.includes(selectedUpsertKey)
        ) {
          sanitizedBulkFields = [...sanitizedBulkFields, selectedUpsertKey];
        }
      }
      const sanitizedBulkRequiredFields = saveBulkFields
        ? buildBulkRequiredFields(
          sanitizedBulkFields,
          (fieldConfig.bulk_action_required_fields || []).filter(fieldName =>
            sanitizedBulkFields.includes(fieldName)
          )
        )
        : [];

      const trimmedListLabel =
        configForm.relationship_label != null ? String(configForm.relationship_label).trim() : '';
      const resolvedListLabel =
        trimmedListLabel ||
        relatedListKindCaption(selectedRelatedList, relatedListTargetCounts) ||
        (selectedRelatedList.relationship_label && String(selectedRelatedList.relationship_label).trim()) ||
        '';

      const configData = {
        layout_id: layoutId,
        related_list_id: relatedListId,
        actions: normalizeActions(configForm.actions),
        max_records: configForm.max_records,
        display_order: configForm.display_order,
        is_active: configForm.is_active,
        relationship_label: resolvedListLabel,
        field_config: {
          ...fieldConfig,
          fields: sanitizedDisplayFields,
          sortBy: fieldConfig.sortBy || "createddate",
          sortOrder: fieldConfig.sortOrder || "DESC",
          required_fields: sanitizedRequiredFields,
          bulk_action_fields: saveBulkFields ? sanitizedBulkFields : [],
          bulk_action_required_fields: saveBulkFields ? sanitizedBulkRequiredFields : [],
          relationship_label: resolvedListLabel
        },
        bulk_action_field_config: saveBulkFields ? {
          ...bulkFieldConfig,
          fields: sanitizedBulkFields,
          sortBy: bulkFieldConfig.sortBy || "createddate",
          sortOrder: bulkFieldConfig.sortOrder || "DESC",
          upsert_key: selectedUpsertKey,
          required_fields: sanitizedBulkRequiredFields
        } : null
      };

      // If we're updating an existing record, include the id
      if (selectedRelatedList.id && !selectedRelatedList.is_temp) {
        configData.id = selectedRelatedList.id;
      }

      const api = new DataApi('relatedlist/layout');
      const response = await api.create(configData);

      if (response?.data?.success) {
        setSuccess('Related list configuration saved successfully');
        fetchLayoutRelatedLists();
        setShowConfigModal(false);
        if (onSave) onSave();
      } else {
        setError('Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      setError('Failed to save configuration: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveRelatedList = async (configId) => {
    if (window.confirm('Are you sure you want to remove this related list from the layout?')) {
      try {
        setLoading(true);
        setError('');

        // If it's a temporary item, just remove from visual list and restore to available
        if (configId.startsWith('temp-')) {
          const tempItem = layoutRelatedLists.find(item => item.id === configId);
          setLayoutRelatedLists(prev => prev.filter(item => item.id !== configId));

          // Restore to available related lists
          if (tempItem) {
            const originalRelatedList = {
              id: tempItem.related_list_id,
              relationship_name: tempItem.relationship_name,
              relationship_label: tempItem.relationship_label,
              target_object_name: tempItem.target_object_name,
              relationship_type: tempItem.relationship_type,
              object_fields: tempItem.object_fields || []
            };
            // Only add if it's not already in the list
            setRelatedLists(prev => {
              const exists = prev.some(rl => rl.id === originalRelatedList.id);
              return exists ? prev : [...prev, originalRelatedList];
            });
          }

          setSuccess('Related list removed');
          return;
        }

        const api = new DataApi('relatedlist/layout');
        const response = await api.delete(configId);

        if (response?.data?.success) {
          setSuccess('Related list removed from layout');
          fetchLayoutRelatedLists();
          fetchRelatedLists(); // Refresh available lists
          if (onSave) onSave();
        } else {
          setError('Failed to remove related list');
        }
      } catch (error) {
        console.error('Error removing related list:', error);
        setError('Failed to remove related list: ' + (error.message || 'Unknown error'));
      } finally {
        setLoading(false);
      }
    }
  };



  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;

    console.log('Drag ended:', { source, destination, draggableId });

    // Handle dragging from available lists to layout lists
    if (source.droppableId === 'available-related-lists' &&
      destination.droppableId === 'layout-related-lists') {
      const relatedListId = draggableId.replace('related-list-', '');
      const relatedList = relatedLists.find(rl => rl.id === relatedListId);
      if (relatedList) {
        console.log('Adding related list to layout:', relatedList);
        handleVisualAddRelatedList(relatedList);
      }
    }

    // Handle reordering within layout panel (related lists + search widgets)
    if (source.droppableId === 'layout-related-lists' &&
      destination.droppableId === 'layout-related-lists') {
      const sourceIndex = source.index;
      const destinationIndex = destination.index;

      const reorderedPanel = Array.from(mergedLayoutPanel);
      const [removed] = reorderedPanel.splice(sourceIndex, 1);
      reorderedPanel.splice(destinationIndex, 0, removed);

      const rlById = new Map(layoutRelatedLists.map((c) => [c.id, { ...c }]));
      const swById = new Map(searchWidgets.map((w) => [w.id, { ...w }]));

      reorderedPanel.forEach((item, index) => {
        if (item.kind === 'rl' && rlById.has(item.id)) {
          rlById.get(item.id).display_order = index;
        } else if (item.kind === 'search' && swById.has(item.id)) {
          swById.get(item.id).display_order = index;
        }
      });

      const nextRl = Array.from(rlById.values()).sort(
        (a, b) => (a.display_order || 0) - (b.display_order || 0)
      );
      const nextSw = Array.from(swById.values()).sort(
        (a, b) => (a.display_order || 0) - (b.display_order || 0)
      );

      setLayoutRelatedLists(nextRl);
      setSearchWidgets(nextSw);
      setSuccess('Order updated — saving...');
      saveUnifiedDisplayOrder(nextRl, nextSw);
    }
  };

  const saveUnifiedDisplayOrder = async (rlList, swList) => {
    try {
      const rlApi = new DataApi('relatedlist/layout');
      const wgApi = new DataApi('layoutwidget');

      const rlPromises = (rlList || [])
        .filter((item) => !item.is_temp && item.id && !String(item.id).startsWith('temp-'))
        .map((item) => {
          const updateData = {
            layout_id: item.layout_id,
            related_list_id: item.related_list_id,
            actions: item.actions,
            max_records: item.max_records,
            display_order: item.display_order,
            is_active: item.is_active,
            field_config: item.field_config,
            bulk_action_field_config: item.bulk_action_field_config ? {
              ...item.bulk_action_field_config,
              fields: item.bulk_action_field_config?.fields || [],
              sortBy: item.bulk_action_field_config?.sortBy || 'createddate',
              sortOrder: item.bulk_action_field_config?.sortOrder || 'DESC',
              upsert_key: item.bulk_action_field_config?.upsert_key || 'id'
            } : null
          };
          return rlApi.update(updateData, item.id);
        });

      const swPromises = (swList || []).map((w) =>
        wgApi.put(`/${w.id}`, { display_order: w.display_order })
      );

      await Promise.all([...rlPromises, ...swPromises]);
      setSuccess('Layout order saved successfully');
      if (onSave) onSave();
    } catch (error) {
      console.error('Error saving unified display order:', error);
      setError('Failed to save order: ' + (error.message || 'Unknown error'));
    }
  };

  const handleRemoveSearchWidget = async (widgetId) => {
    if (!window.confirm('Remove this search widget from the layout?')) return;
    try {
      setLoading(true);
      setError('');
      const api = new DataApi('layoutwidget');
      const response = await api.delete(widgetId);
      if (response?.data?.success) {
        setSuccess('Search widget removed');
        await fetchLayoutSearchWidgets();
        if (onSave) onSave();
      } else {
        setError(response?.data?.message || 'Failed to remove search widget');
      }
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to remove search widget');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;

    console.log('Field drag ended:', { source, destination, draggableId });

    const sourceMode = source.droppableId.includes('bulk') ? 'bulk' : 'list';
    const destinationMode = destination.droppableId.includes('bulk') ? 'bulk' : 'list';

    // Only handle drags within the same configuration mode
    if (sourceMode !== destinationMode) return;

    const configKey = sourceMode === 'bulk' ? 'bulk_action_field_config' : 'field_config';
    const selectedFields = configForm[configKey]?.fields || [];

    // Extract clean field name regardless of which list it came from
    const fieldName = draggableId
      .replace('field-', '')
      .replace(`selected-${fieldConfigMode}-`, '')
      .replace(`selected-list-`, '')
      .replace(`selected-bulk-`, '');

    // Handle dragging from available fields to selected fields
    if (source.droppableId.startsWith('available-fields') &&
      destination.droppableId.startsWith('selected-fields')) {

      if (selectedFields.includes(fieldName)) return;
      setConfigForm(prev => {
        const currentConfig = prev[configKey] || {};
        const currentFields = Array.from(currentConfig.fields || []);

        // Insert at the specific position dropped
        currentFields.splice(destination.index, 0, fieldName);

        const nextFields = configKey === 'bulk_action_field_config'
          ? enforceRequiredBulkFields(currentFields)
          : currentFields;

        const nextState = {
          ...prev,
          [configKey]: {
            ...currentConfig,
            fields: nextFields
          }
        };
        if (configKey === 'bulk_action_field_config') {
          nextState.field_config = {
            ...prev.field_config,
            bulk_action_fields: nextFields
          };
        }
        return nextState;
      });
    }

    // 2. Handle dragging from selected fields to available fields (Right to Left / Remove)
    if (source.droppableId.startsWith('selected-fields') &&
      destination.droppableId.startsWith('available-fields')) {

      // Check if removable (bulk required or upsert key)
      const isBulkRequiredField = bulkRequiredFieldNames.includes(fieldName);
      const isBulkUpsertKey = configKey === 'bulk_action_field_config' && configForm.bulk_action_field_config?.upsert_key === fieldName;
      if (configKey === 'bulk_action_field_config' && (isBulkRequiredField || isBulkUpsertKey)) return;

      setConfigForm(prev => {
        const currentConfig = prev[configKey] || {};
        const currentFields = (currentConfig.fields || []).filter(f => f !== fieldName);

        const nextFields = configKey === 'bulk_action_field_config'
          ? enforceRequiredBulkFields(currentFields)
          : currentFields;

        const nextState = {
          ...prev,
          [configKey]: {
            ...currentConfig,
            fields: nextFields
          }
        };
        if (configKey === 'bulk_action_field_config') {
          nextState.field_config = {
            ...prev.field_config,
            bulk_action_fields: nextFields
          };
        }
        return nextState;
      });
      return;
    }
    // Handle reordering within selected fields
    if (source.droppableId.startsWith('selected-fields') &&
      destination.droppableId.startsWith('selected-fields')) {
      const sourceIndex = source.index;
      const destinationIndex = destination.index;

      setConfigForm(prev => {
        const currentConfig = prev[configKey] || {};
        const currentFields = currentConfig.fields || [];
        const reorderedFields = Array.from(currentFields);
        const [removed] = reorderedFields.splice(sourceIndex, 1);
        reorderedFields.splice(destinationIndex, 0, removed);
        const nextFields = configKey === 'bulk_action_field_config'
          ? enforceRequiredBulkFields(reorderedFields)
          : reorderedFields;
        const nextState = {
          ...prev,
          [configKey]: {
            ...currentConfig,
            fields: nextFields
          }
        };
        if (configKey === 'bulk_action_field_config') {
          nextState.field_config = {
            ...prev.field_config,
            bulk_action_fields: nextFields
          };
        }
        return nextState;
      });
    }
  };


  const isRelatedListAssigned = (relatedListId) => {
    return layoutRelatedLists.some(config => config.related_list_id === relatedListId);
  };

  // Check if Activity Timeline has already been added
  const isActivityTimelineAdded = () => {
    // Check if any layout related list has a related_list that points to activity with the right fields
    return layoutRelatedLists.some(config => {
      // Check if the related list in available lists matches activity timeline pattern
      const matchingRelatedList = relatedLists.find(rl => rl.id === config.related_list_id);
      return matchingRelatedList &&
        matchingRelatedList.target_object_name === 'activity' &&
        matchingRelatedList.source_field_name === 'id' &&
        matchingRelatedList.target_field_name === 'related_record_id';
    });
  };

  // Get available related lists (omit assigned; omit system user lookups — owner / audit)
  const availableRelatedLists = relatedLists.filter(
    (relatedList) =>
      !isRelatedListAssigned(relatedList.id) &&
      !isHiddenSystemUserRelationship(relatedList)
  );
  const activeFieldConfigKey = fieldConfigMode === 'bulk' ? 'bulk_action_field_config' : 'field_config';
  const objectFields = selectedRelatedList?.object_fields || [];
  const requiredFieldNames = useMemo(() => {
    return (objectFields || [])
      .filter(field => isFieldRequired(field))
      .map(field => field.field_name)
      .filter(fieldName => {
        if (!fieldName) return false;
        return fieldName.toLowerCase() !== 'ownerid';
      });
  }, [objectFields]);
  const bulkSelectableFields = objectFields.filter(field => !isFieldReadonly(field));
  const buildListRequiredFields = useCallback((fields = [], prevRequired = []) => {
    const baseRequired = requiredFieldNames.filter(name => fields.includes(name));
    const seen = new Set(baseRequired);
    const result = [...baseRequired];
    prevRequired.forEach(name => {
      if (!name || seen.has(name) || !fields.includes(name)) return;
      seen.add(name);
      result.push(name);
    });
    return result;
  }, [requiredFieldNames]);
  const bulkRequiredFieldNames = useMemo(() => {
    if (!bulkSelectableFields.length) return [];
    const selectableSet = new Set(bulkSelectableFields.map(field => field.field_name));
    return requiredFieldNames.filter(name => selectableSet.has(name));
  }, [requiredFieldNames, bulkSelectableFields]);
  const buildBulkRequiredFields = useCallback((fields = [], prevRequired = []) => {
    const baseRequired = bulkRequiredFieldNames.filter(name => fields.includes(name));
    const seen = new Set(baseRequired);
    const result = [...baseRequired];
    prevRequired.forEach(name => {
      if (!name || seen.has(name) || !fields.includes(name)) return;
      seen.add(name);
      result.push(name);
    });
    return result;
  }, [bulkRequiredFieldNames]);
  const enforceRequiredBulkFields = useCallback((fields = [], { includeRequired = true } = {}) => {
    const result = [];
    const seen = new Set();

    if (Array.isArray(fields)) {
      fields.forEach(fieldName => {
        if (!fieldName || seen.has(fieldName)) return;
        seen.add(fieldName);
        result.push(fieldName);
      });
    }

    if (includeRequired) {
      bulkRequiredFieldNames.forEach(fieldName => {
        if (!fieldName || seen.has(fieldName)) return;
        seen.add(fieldName);
        result.push(fieldName);
      });
    }

    return result;
  }, [bulkRequiredFieldNames]);
  const bulkUpsertOptions = Array.from(new Set([
    'id',
    ...objectFields
      .filter(field => field.is_unique && field.field_name)
      .map(field => field.field_name)
  ])).filter(Boolean);
  const activeFieldConfig = configForm[activeFieldConfigKey] || {
    fields: [],
    sortBy: 'createddate',
    sortOrder: 'DESC',
    upsert_key: 'id'
  };
  const availableFieldsForMode = fieldConfigMode === 'bulk' ? bulkSelectableFields : objectFields;
  const normalizedFieldSearch = fieldSearchTerm.trim().toLowerCase();
  const filteredAvailableFields = availableFieldsForMode.filter(field => {
    if (!normalizedFieldSearch) return true;
    const name = String(field.field_name || '').toLowerCase();
    const label = String(field.field_label || '').toLowerCase();
    return name.includes(normalizedFieldSearch) || label.includes(normalizedFieldSearch);
  });
  const displayedAvailableFields = filteredAvailableFields.filter(
    field => !activeFieldConfig.fields.includes(field.field_name)
  );
  const isFieldSearchActive = normalizedFieldSearch.length > 0;
  const hasBulkPrereqs = bulkSelectableFields.length > 0 && (configForm.field_config?.fields?.length || 0) > 0;
  const canConfigureBulk = saveBulkFields && hasBulkPrereqs;

  useEffect(() => {
    if (!selectedRelatedList || !saveBulkFields) return;
    setConfigForm(prev => {
      const currentFields = prev.bulk_action_field_config?.fields || [];
      const enforcedFields = enforceRequiredBulkFields(currentFields);
      if (arraysEqual(currentFields, enforcedFields)) {
        return prev;
      }
      return {
        ...prev,
        bulk_action_field_config: {
          ...prev.bulk_action_field_config,
          fields: enforcedFields
        },
        field_config: {
          ...prev.field_config,
          bulk_action_fields: enforcedFields
        }
      };
    });
  }, [selectedRelatedList, saveBulkFields, enforceRequiredBulkFields]);

  useEffect(() => {
    setConfigForm(prev => {
      if (!selectedRelatedList) return prev;
      const fieldConfig = prev.field_config || {};
      const fields = fieldConfig.fields || [];
      const currentRequired = fieldConfig.required_fields || [];
      const nextRequired = buildListRequiredFields(fields, currentRequired);
      const bulkFields = fieldConfig.bulk_action_fields || [];
      const currentBulkRequired = fieldConfig.bulk_action_required_fields || [];
      const nextBulkRequired = buildBulkRequiredFields(bulkFields, currentBulkRequired);
      const bulkActionConfig = prev.bulk_action_field_config;
      const bulkConfigRequired = bulkActionConfig?.required_fields || [];
      const shouldUpdate =
        !arraysEqual(nextRequired, currentRequired) ||
        !arraysEqual(nextBulkRequired, currentBulkRequired) ||
        (bulkActionConfig && !arraysEqual(nextBulkRequired, bulkConfigRequired));
      if (!shouldUpdate) {
        return prev;
      }
      return {
        ...prev,
        field_config: {
          ...fieldConfig,
          required_fields: nextRequired,
          bulk_action_required_fields: nextBulkRequired
        },
        bulk_action_field_config: bulkActionConfig
          ? {
            ...bulkActionConfig,
            required_fields: nextBulkRequired
          }
          : null
      };
    });
  }, [
    selectedRelatedList,
    buildListRequiredFields,
    buildBulkRequiredFields,
    configForm.field_config?.fields,
    configForm.field_config?.bulk_action_fields
  ]);

  useEffect(() => {
    if (fieldConfigMode === 'bulk' && !canConfigureBulk) {
      setFieldConfigMode('list');
    }
  }, [fieldConfigMode, canConfigureBulk]);

  return (
    <>
      <Modal show={true} onHide={onClose} size="xl" className="related-list-manager-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <FaLayerGroup className="me-2" />
            Related List Manager
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="related-list-manager-content">
            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

            <DragDropContext onDragEnd={handleDragEnd}>
              <Row>
                {/* Available Related Lists */}
                <Col md={6}>
                  <Card>
                    <Card.Header className="text-white">
                      <h6 className="mb-0">
                        <FaList className="me-2" />
                        Related lists &amp; search
                      </h6>
                      <small className="d-block mt-1 fw-normal opacity-75">
                        Add relationship cards, Activity Timeline, or a search widget (same setup as Manage Widgets).
                      </small>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <div className="related-lists-scroll-container">
                        <div
                          className="related-list-item"
                          style={{ margin: '8px', padding: '12px', border: '1px solid #dee2e6', borderRadius: '4px', cursor: 'pointer' }}
                          onClick={() => {
                            setEditingSearchWidget(null);
                            setShowSearchWidgetModal(true);
                          }}
                        >
                          <div className="related-list-content">
                            <div className="related-list-header">
                              <h6 className="mb-1">Search widget</h6>
                              <Badge bg="info">Search</Badge>
                            </div>
                            <div className="related-list-details">
                              <small className="text-muted">
                                Embed search results on the detail layout: choose a search view, columns, and filters bound to this record. Configure views under Setup → Search Views.
                              </small>
                            </div>
                          </div>
                          <div className="related-list-actions">
                            <Button
                              size="sm"
                              variant="outline-success"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingSearchWidget(null);
                                setShowSearchWidgetModal(true);
                              }}
                              title="Add search widget"
                            >
                              <FaPlus />
                            </Button>
                          </div>
                        </div>

                        {/* Activity Timeline Option - Only show if not already added */}
                        {!isActivityTimelineAdded() && (
                          <div className="related-list-item" style={{ margin: '8px', padding: '12px', border: '1px solid #dee2e6', borderRadius: '4px', cursor: 'pointer' }}
                            onClick={() => handleAddActivityTimeline()}
                          >
                            <div className="related-list-content">
                              <div className="related-list-header">
                                <h6 className="mb-1">Activity Timeline</h6>
                                <Badge bg="primary">Activity</Badge>
                              </div>
                              <div className="related-list-details">
                                <small className="text-muted">
                                  Display timeline of activities (tasks, emails, notes) related to this record
                                </small>
                              </div>
                            </div>
                            <div className="related-list-actions">
                              <Button
                                size="sm"
                                variant="outline-success"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddActivityTimeline();
                                }}
                                title="Add Activity Timeline to Layout"
                              >
                                <FaPlus />
                              </Button>
                            </div>
                          </div>
                        )}

                        <Droppable droppableId="available-related-lists">
                          {(provided) => (
                            <div
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              className="related-lists-container"
                            >
                              {availableRelatedLists.map((relatedList, index) => {
                                const kindCaption = relatedListKindCaption(relatedList, relatedListTargetCounts);
                                const relLabel = relatedList.relationship_label && String(relatedList.relationship_label).trim();
                                const showLayoutLabelHint = relLabel && relLabel !== kindCaption;
                                return (
                                  <Draggable
                                    key={relatedList.id}
                                    draggableId={`related-list-${relatedList.id}`}
                                    index={index}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className={`related-list-item ${snapshot.isDragging ? 'dragging' : ''}`}
                                      >
                                        <div className="related-list-content">
                                          <div className="related-list-header d-flex flex-wrap align-items-start justify-content-between gap-2">
                                            <h6 className="mb-0">{kindCaption}</h6>
                                            <div className="d-flex flex-wrap gap-1 justify-content-end">
                                              <Badge bg="secondary">{relatedList.target_object_name}</Badge>
                                              {relatedListTargetFieldApi(relatedList) && (
                                                <Badge
                                                  bg="dark"
                                                  className="fw-normal"
                                                  title="Target field: column on the related object used for this relationship"
                                                >
                                                  {relatedListTargetFieldApi(relatedList)}
                                                </Badge>
                                              )}
                                            </div>
                                          </div>
                                          <div className="related-list-details">
                                            <small className="text-muted">
                                              {relatedList.relationship_type} • {relatedList.object_fields?.length || 0} fields
                                              {showLayoutLabelHint && (
                                                <>
                                                  <br />
                                                  Tab / list label: {relLabel}
                                                </>
                                              )}
                                            </small>
                                          </div>
                                        </div>
                                        <div className="related-list-actions">
                                          <Button
                                            size="sm"
                                            variant="outline-success"
                                            onClick={() => handleVisualAddRelatedList(relatedList)}
                                            title="Add to Layout"
                                          >
                                            <FaPlus />
                                          </Button>
                                          <FaGripVertical className="drag-handle" />
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Layout panel: related list tabs + search widgets (unified order) */}
                <Col md={6}>
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">
                        <FaLayerGroup className="me-2" />
                        Layout related lists &amp; search
                      </h6>
                      <small className="d-block mt-1 fw-normal opacity-75">
                        Drag to set order. Related lists control tabs; search widgets share the same ordering index for this layout.
                      </small>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <div className="layout-related-lists-scroll-container">
                        <Droppable droppableId="layout-related-lists">
                          {(provided, snapshot) => (
                            <div
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              className={`layout-related-lists-container ${snapshot.isDraggingOver ? 'drag-over' : ''}`}
                            >
                              {mergedLayoutPanel.length === 0 ? (
                                <div className="empty-state">
                                  <FaLayerGroup className="empty-icon" />
                                  <p>Drag related lists here or add a search widget from the left</p>
                                </div>
                              ) : (
                                mergedLayoutPanel.map((panelItem, index) => {
                                  if (panelItem.kind === 'search') {
                                    const widget = panelItem.widget;
                                    const cfg = widget.config || {};
                                    return (
                                      <Draggable
                                        key={`search-${widget.id}`}
                                        draggableId={`panel-search-${widget.id}`}
                                        index={index}
                                      >
                                        {(provided, snapshot) => (
                                          <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            className={`layout-related-list-item ${snapshot.isDragging ? 'dragging' : ''}`}
                                          >
                                            <div className="layout-related-list-content">
                                              <div className="layout-related-list-header">
                                                <h6 className="mb-1">{widget.widget_label || 'Search'}</h6>
                                                <div className="layout-related-list-badges">
                                                  <Badge bg="info">Search widget</Badge>
                                                  <Badge bg="secondary">Column order: {widget.display_order}</Badge>
                                                  {widget.is_active ? (
                                                    <Badge bg="success">Active</Badge>
                                                  ) : (
                                                    <Badge bg="warning">Inactive</Badge>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="layout-related-list-details">
                                                <small className="text-muted">
                                                  Column order: {widget.display_order} •
                                                  Search view: {cfg.search_view_name || cfg.search_view_id || '—'} •
                                                  Fields: {(cfg.fields || []).length}
                                                </small>
                                              </div>
                                            </div>
                                            <div className="layout-related-list-actions">
                                              <Button
                                                size="sm"
                                                variant="outline-primary"
                                                onClick={() => {
                                                  setEditingSearchWidget(widget);
                                                  setShowSearchWidgetModal(true);
                                                }}
                                                title="Edit search widget"
                                              >
                                                <FaCog />
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="outline-danger"
                                                onClick={() => handleRemoveSearchWidget(widget.id)}
                                                title="Remove search widget"
                                              >
                                                <FaTrash />
                                              </Button>
                                              <div {...provided.dragHandleProps} className="drag-handle">
                                                <FaGripVertical />
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </Draggable>
                                    );
                                  }

                                  const config = panelItem.rl;
                                  const isActivityTimeline = config.activity_timeline === true || config.relationship_label === 'Activity Timeline';
                                  const layoutKindCaption = relatedListKindCaption(config, relatedListTargetCounts);
                                  const layoutRelLabel = config.relationship_label && String(config.relationship_label).trim();
                                  const showLayoutPanelLabelHint =
                                    !isActivityTimeline && layoutRelLabel && layoutRelLabel !== layoutKindCaption;

                                  return (
                                    <Draggable
                                      key={`rl-${config.id}`}
                                      draggableId={`panel-rl-${config.id}`}
                                      index={index}
                                    >
                                      {(provided, snapshot) => (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          className={`layout-related-list-item ${snapshot.isDragging ? 'dragging' : ''}`}
                                        >
                                          <div className="layout-related-list-content">
                                            <div className="layout-related-list-header">
                                              <h6 className="mb-1">
                                                {isActivityTimeline ? config.relationship_label : layoutKindCaption}
                                              </h6>
                                              <div className="layout-related-list-badges">
                                                {isActivityTimeline ? (
                                                  <Badge bg="primary">Activity Timeline</Badge>
                                                ) : (
                                                  <>
                                                    <Badge bg="secondary" title="Related object API name">
                                                      {config.target_object_name}
                                                    </Badge>
                                                    {relatedListTargetFieldApi(config) && (
                                                      <Badge
                                                        bg="dark"
                                                        className="fw-normal"
                                                        title="Target field: column on the related object used for this relationship"
                                                      >
                                                        {relatedListTargetFieldApi(config)}
                                                      </Badge>
                                                    )}
                                                    <Badge bg="info">{config.max_records} records</Badge>
                                                    <Badge bg="secondary">Order: {config.display_order}</Badge>
                                                  </>
                                                )}
                                                {config.is_temp && (
                                                  <Badge bg="warning" text="dark">Not Saved</Badge>
                                                )}
                                                {config.is_active ? (
                                                  <Badge bg="success">Active</Badge>
                                                ) : (
                                                  <Badge bg="warning">Inactive</Badge>
                                                )}
                                              </div>
                                            </div>
                                            <div className="layout-related-list-details">
                                              <small className="text-muted">
                                                {isActivityTimeline ? (
                                                  'Displays timeline of activities (tasks, emails, notes) related to this record'
                                                ) : (
                                                  <>
                                                    Fields: {config.field_config?.fields?.length || 0} •
                                                    Bulk Fields: {config.bulk_action_field_config?.fields?.length || 0} •
                                                    Upsert: {config.bulk_action_field_config?.upsert_key || 'id'} •
                                                    Actions: {config.actions?.length || 0}
                                                    {showLayoutPanelLabelHint && (
                                                      <>
                                                        <br />
                                                        Tab / list label: {layoutRelLabel}
                                                      </>
                                                    )}
                                                  </>
                                                )}
                                              </small>
                                            </div>
                                          </div>
                                          <div className="layout-related-list-actions">
                                            {isActivityTimeline ? (
                                              <>
                                                <Button
                                                  size="sm"
                                                  variant="outline-danger"
                                                  onClick={() => handleRemoveRelatedList(config.id)}
                                                  title="Remove Activity Timeline"
                                                >
                                                  <FaTrash />
                                                </Button>
                                                <div {...provided.dragHandleProps} className="drag-handle">
                                                  <FaGripVertical />
                                                </div>
                                              </>
                                            ) : (
                                              <>
                                                <Button
                                                  size="sm"
                                                  variant="outline-primary"
                                                  onClick={() => {
                                                    let objectFields = config.object_fields || [];

                                                    if (!objectFields.length && !config.is_temp) {
                                                      const originalRelatedList = relatedLists.find(rl => rl.id === config.related_list_id);
                                                      if (originalRelatedList) {
                                                        objectFields = originalRelatedList.object_fields || [];
                                                      }
                                                    }

                                                    const allFieldNames = objectFields
                                                      ? objectFields.map(field => field.field_name)
                                                      : [];
                                                    const editableFieldNames = objectFields
                                                      ? objectFields.filter(field => !isFieldReadonly(field)).map(field => field.field_name)
                                                      : [];
                                                    const sanitizedDisplayFields = Array.isArray(config.field_config?.fields)
                                                      ? config.field_config.fields.filter(fieldName => allFieldNames.includes(fieldName))
                                                      : allFieldNames;
                                                    const existingBulkFieldsSource =
                                                      (config.bulk_action_field_config?.fields && Array.isArray(config.bulk_action_field_config.fields)
                                                        ? config.bulk_action_field_config.fields
                                                        : config.field_config?.bulk_action_fields) || [];
                                                    const sanitizedBulkFields = enforceRequiredBulkFields(
                                                      existingBulkFieldsSource.filter(fieldName =>
                                                        editableFieldNames.includes(fieldName)
                                                      ),
                                                      {
                                                        includeRequired:
                                                          !!config.bulk_action_field_config ||
                                                          !!(config.field_config?.bulk_action_fields && config.field_config.bulk_action_fields.length)
                                                      }
                                                    );
                                                    const sanitizedRequiredFields = Array.isArray(config.field_config?.required_fields)
                                                      ? config.field_config.required_fields.filter(fieldName =>
                                                        sanitizedDisplayFields.includes(fieldName)
                                                      )
                                                      : getBaseRequiredFieldNames(objectFields);
                                                    const existingBulkRequiredSource =
                                                      (config.bulk_action_field_config?.required_fields && Array.isArray(config.bulk_action_field_config.required_fields)
                                                        ? config.bulk_action_field_config.required_fields
                                                        : config.field_config?.bulk_action_required_fields) || [];
                                                    const sanitizedBulkRequiredFields = existingBulkRequiredSource.filter(fieldName =>
                                                      sanitizedBulkFields.includes(fieldName)
                                                    );
                                                    const upsertKey = config.bulk_action_field_config?.upsert_key || "id";
                                                    const fieldConfig = config.field_config || {
                                                      fields: [],
                                                      sortBy: "createddate",
                                                      sortOrder: "DESC"
                                                    };
                                                    const bulkFieldConfig = config.bulk_action_field_config || {
                                                      fields: [],
                                                      sortBy: "createddate",
                                                      sortOrder: "DESC",
                                                      upsert_key: "id"
                                                    };

                                                    const rlForCaption = { ...config, object_fields: objectFields };
                                                    const storedListLabel =
                                                      (config.relationship_label && String(config.relationship_label).trim()) ||
                                                      (fieldConfig.relationship_label && String(fieldConfig.relationship_label).trim()) ||
                                                      '';
                                                    const initialListLabel =
                                                      storedListLabel || relatedListKindCaption(rlForCaption, relatedListTargetCounts) || '';

                                                    setSelectedRelatedList({
                                                      ...config,
                                                      object_fields: objectFields
                                                    });
                                                    setSaveBulkFields(
                                                      (!!config.bulk_action_field_config && sanitizedBulkFields.length > 0) ||
                                                      (!!(config.field_config?.bulk_action_fields) && config.field_config.bulk_action_fields.length > 0)
                                                    );
                                                    setConfigForm({
                                                      actions: config.actions || [],
                                                      max_records: config.max_records || 50,
                                                      display_order: config.display_order || 0,
                                                      is_active: config.is_active,
                                                      relationship_label: initialListLabel,
                                                      field_config: {
                                                        ...fieldConfig,
                                                        fields: sanitizedDisplayFields,
                                                        sortBy: fieldConfig.sortBy || "createddate",
                                                        sortOrder: fieldConfig.sortOrder || "DESC",
                                                        required_fields: sanitizedRequiredFields,
                                                        bulk_action_fields: sanitizedBulkFields,
                                                        bulk_action_required_fields: sanitizedBulkRequiredFields,
                                                        relationship_label: initialListLabel
                                                      },
                                                      bulk_action_field_config: sanitizedBulkFields.length > 0 ? {
                                                        ...bulkFieldConfig,
                                                        fields: sanitizedBulkFields,
                                                        sortBy: bulkFieldConfig.sortBy || "createddate",
                                                        sortOrder: bulkFieldConfig.sortOrder || "DESC",
                                                        upsert_key: upsertKey,
                                                        required_fields: sanitizedBulkRequiredFields
                                                      } : null
                                                    });
                                                    setFieldConfigMode('list');
                                                    setShowConfigModal(true);
                                                  }}
                                                  title="Configure Related List"
                                                >
                                                  <FaCog />
                                                </Button>
                                                <Button
                                                  size="sm"
                                                  variant="outline-danger"
                                                  onClick={() => handleRemoveRelatedList(config.id)}
                                                  title="Remove from Layout"
                                                >
                                                  <FaTrash />
                                                </Button>
                                                <div {...provided.dragHandleProps} className="drag-handle">
                                                  <FaGripVertical />
                                                </div>
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </Draggable>
                                  );
                                })
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </DragDropContext>
          </div>

          {/* Configuration Modal */}
          <Modal show={showConfigModal} onHide={() => setShowConfigModal(false)} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>
                <FaCog className="me-2" />
                Configure Related List
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <DragDropContext onDragEnd={handleFieldDragEnd}>
                {selectedRelatedList && (
                  <Form>
                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Related List</Form.Label>
                          <Form.Control
                            type="text"
                            value={configForm.relationship_label || ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setConfigForm({
                                ...configForm,
                                relationship_label: v,
                                field_config: {
                                  ...configForm.field_config,
                                  relationship_label: v
                                }
                              });
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Max Records</Form.Label>
                          <Form.Control
                            type="number"
                            value={configForm.max_records}
                            onChange={(e) => setConfigForm({
                              ...configForm,
                              max_records: parseInt(e.target.value) || 50
                            })}
                            min="1"
                            max="1000"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Active"
                        checked={configForm.is_active}
                        onChange={(e) => setConfigForm({
                          ...configForm,
                          is_active: e.target.checked
                        })}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Configure Fields</Form.Label>
                      <div className="d-flex align-items-center mb-3">
                        <ButtonGroup>
                          <Button
                            variant={fieldConfigMode === 'list' ? 'primary' : 'outline-primary'}
                            onClick={() => setFieldConfigMode('list')}
                          >
                            Related List Fields
                          </Button>
                        </ButtonGroup>
                      </div>
                      <div className="field-drag-drop-container">
                        <Row>
                          <Col md={6}>
                            <div className="available-fields-section">
                              <h6 className="mb-2">Available Fields</h6>
                              <InputGroup className="mb-2">
                                <InputGroup.Text>
                                  <FaSearch />
                                </InputGroup.Text>
                                <Form.Control
                                  type="text"
                                  placeholder={fieldConfigMode === 'bulk' ? 'Search editable fields...' : 'Search fields...'}
                                  value={fieldSearchTerm}
                                  onChange={(e) => setFieldSearchTerm(e.target.value)}
                                />
                              </InputGroup>
                              <Droppable droppableId={`available-fields-${fieldConfigMode}`}>
                                {(provided) => (
                                  <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    className="available-fields-list"
                                  >
                                    {displayedAvailableFields.length > 0 ? (
                                      displayedAvailableFields
                                        .map((field, index) => (
                                          <Draggable
                                            key={`${fieldConfigMode}-${field.field_name}`}
                                            draggableId={`field-${field.field_name}`}
                                            index={index}
                                          >
                                            {(provided, snapshot) => (
                                              <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className={`field-item mt-2 available-field ${snapshot.isDragging ? 'dragging' : ''}`}
                                              >
                                                <div
                                                  className="field-content"
                                                  style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between'
                                                  }}
                                                >
                                                  <span className="field-label">
                                                    {field.field_label}
                                                    <small className="text-muted"> ({field.field_name})</small>
                                                  </span>

                                                  <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    onClick={() => {
                                                      setConfigForm(prev => {
                                                        const currentConfig = prev[activeFieldConfigKey] || {};
                                                        const currentFields = currentConfig.fields || [];
                                                        if (currentFields.includes(field.field_name)) return prev;

                                                        const appendedFields = [...currentFields, field.field_name];
                                                        const nextFields =
                                                          activeFieldConfigKey === 'bulk_action_field_config'
                                                            ? enforceRequiredBulkFields(appendedFields)
                                                            : appendedFields;

                                                        const nextState = {
                                                          ...prev,
                                                          [activeFieldConfigKey]: {
                                                            ...currentConfig,
                                                            fields: nextFields
                                                          }
                                                        };

                                                        if (activeFieldConfigKey === 'bulk_action_field_config') {
                                                          nextState.field_config = {
                                                            ...prev.field_config,
                                                            bulk_action_fields: nextFields
                                                          };
                                                        }

                                                        return nextState;
                                                      });
                                                    }}
                                                  >
                                                    <FaPlus />
                                                  </Button>
                                                </div>
                                              </div>

                                            )}
                                          </Draggable>
                                        ))
                                    ) : (
                                      <div className="text-muted">
                                        <small>
                                          {isFieldSearchActive
                                            ? 'No fields match your search.'
                                            : 'No fields available.'}
                                        </small>
                                      </div>
                                    )}
                                    {provided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            </div>
                          </Col>
                          <Col md={6}>
                            <div className="selected-fields-section">
                              <h6 className="mb-2">Selected Fields</h6>
                              <Droppable droppableId={`selected-fields-${fieldConfigMode}`}>
                                {(provided, snapshot) => (
                                  <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    className={`selected-fields-list ${snapshot.isDraggingOver ? 'drag-over' : ''}`}
                                  >
                                    {activeFieldConfig.fields.length > 0 ? (
                                      activeFieldConfig.fields
                                        .filter(fieldName => !!objectFields.find(f => f.field_name === fieldName))
                                        .map((fieldName, index) => {
                                          const field = objectFields.find(f => f.field_name === fieldName);
                                          const isBulkRequiredField = bulkRequiredFieldNames.includes(fieldName);
                                          const isBulkUpsertKey = fieldConfigMode === 'bulk' && configForm.bulk_action_field_config?.upsert_key === fieldName;
                                          const disableRemove =
                                            fieldConfigMode === 'bulk' &&
                                            (isBulkRequiredField || isBulkUpsertKey);

                                          return (
                                            <Draggable
                                              key={`selected-${fieldConfigMode}-${fieldName}`}
                                              draggableId={`selected-${fieldConfigMode}-${fieldName}`}
                                              index={index}
                                            >
                                              {(dragProvided, dragSnapshot) => (
                                                <div
                                                  ref={dragProvided.innerRef}
                                                  {...dragProvided.draggableProps}
                                                  className={`field-item rounded border border-light mt-1 selected-field ${dragSnapshot.isDragging ? 'dragging' : ''}`}
                                                >
                                                  <div
                                                    className="field-content"
                                                    style={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'space-between'
                                                    }}
                                                  >
                                                    {/* LEFT SIDE */}
                                                    <div
                                                      style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px'
                                                      }}
                                                    >
                                                      <div {...dragProvided.dragHandleProps} className="drag-handle">
                                                        <FaGripVertical />
                                                      </div>

                                                      <span className="field-label">
                                                        {field.field_label}
                                                        <small className="text-muted"> ({field.field_name})</small>
                                                      </span>
                                                    </div>

                                                    {/* RIGHT SIDE */}
                                                    <Button
                                                      size="sm"
                                                      variant="outline-danger"
                                                      disabled={disableRemove}
                                                      title={
                                                        disableRemove
                                                          ? isBulkUpsertKey
                                                            ? 'The upsert key field cannot be removed.'
                                                            : 'Required fields cannot be removed.'
                                                          : undefined
                                                      }
                                                      onClick={() => {
                                                        if (disableRemove) return;

                                                        setConfigForm(prev => {
                                                          const currentConfig = prev[activeFieldConfigKey] || {};
                                                          const currentFields = (currentConfig.fields || []).filter(f => f !== fieldName);
                                                          const nextFields =
                                                            activeFieldConfigKey === 'bulk_action_field_config'
                                                              ? enforceRequiredBulkFields(currentFields)
                                                              : currentFields;

                                                          const nextState = {
                                                            ...prev,
                                                            [activeFieldConfigKey]: {
                                                              ...currentConfig,
                                                              fields: nextFields
                                                            }
                                                          };

                                                          if (activeFieldConfigKey === 'bulk_action_field_config') {
                                                            nextState.field_config = {
                                                              ...prev.field_config,
                                                              bulk_action_fields: nextFields
                                                            };
                                                          }

                                                          return nextState;
                                                        });
                                                      }}
                                                    >
                                                      <FaTimes />
                                                    </Button>
                                                  </div>
                                                </div>
                                              )}
                                            </Draggable>
                                          );
                                        })
                                    ) : (
                                      <div className="text-muted">
                                        <small>No fields selected. Drag fields here or use the + button.</small>
                                      </div>
                                    )}
                                    {provided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Sort Configuration</Form.Label>
                      <div className="field-config-section">
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-2">
                              <Form.Label>Sort By Field</Form.Label>
                              <Form.Select
                                value={activeFieldConfig.sortBy}
                                onChange={(e) => setConfigForm({
                                  ...configForm,
                                  [activeFieldConfigKey]: {
                                    ...activeFieldConfig,
                                    sortBy: e.target.value
                                  }
                                })}
                              >
                                <option value="createddate">Created Date</option>
                                <option value="lastmodifieddate">Last Modified Date</option>
                                <option value="id">ID</option>
                                {selectedRelatedList.object_fields?.map((field) => (
                                  <option key={field.field_name} value={field.field_name}>
                                    {field.field_label}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-2">
                              <Form.Label>Sort Order</Form.Label>
                              <Form.Select
                                value={activeFieldConfig.sortOrder}
                                onChange={(e) => setConfigForm({
                                  ...configForm,
                                  [activeFieldConfigKey]: {
                                    ...activeFieldConfig,
                                    sortOrder: e.target.value
                                  }
                                })}
                              >
                                <option value="ASC">Ascending</option>
                                <option value="DESC">Descending</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                        {fieldConfigMode === 'bulk' && (
                          <Row className="mt-2">
                            <Col md={12}>
                              <Form.Group className="mb-2">
                                <Form.Label>Upsert Key Field</Form.Label>
                                <Form.Select
                                  value={configForm.bulk_action_field_config?.upsert_key || 'id'}
                                  onChange={(e) => {
                                    const newKey = e.target.value;
                                    setConfigForm({
                                      ...configForm,
                                      bulk_action_field_config: {
                                        ...configForm.bulk_action_field_config,
                                        upsert_key: newKey
                                      }
                                    });
                                  }}
                                >
                                  {bulkUpsertOptions.map((option) => {
                                    const optionField = objectFields.find(field => field.field_name === option);
                                    const label = option === 'id'
                                      ? 'ID'
                                      : optionField
                                        ? `${optionField.field_label} (${optionField.field_name})`
                                        : option;
                                    return (
                                      <option key={option} value={option}>
                                        {label}
                                      </option>
                                    );
                                  })}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                  This field acts as the identifier when performing bulk upsert operations.
                                </Form.Text>
                              </Form.Group>
                            </Col>
                          </Row>
                        )}
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Available Actions</Form.Label>
                      <div className="actions-selection">
                        {DEFAULT_RELATED_LIST_ACTIONS.map((action) => (
                          <Form.Check
                            key={action}
                            type="checkbox"
                            label={formatActionLabel(action)}
                            checked={configForm.actions.includes(action)}
                            onChange={(e) => {
                              const newActions = e.target.checked
                                ? normalizeActions([...configForm.actions, action])
                                : normalizeActions(configForm.actions.filter(a => a !== action));
                              setConfigForm({
                                ...configForm,
                                actions: newActions
                              });
                            }}
                          />
                        ))}
                      </div>
                    </Form.Group>
                  </Form>
                )}
              </DragDropContext>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowConfigModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveConfig}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Configuration'}
              </Button>
            </Modal.Footer>
          </Modal>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <SearchWidgetConfigModal
        show={showSearchWidgetModal}
        onHide={() => {
          setShowSearchWidgetModal(false);
          setEditingSearchWidget(null);
        }}
        relatedListManagerContext
        objectName={objectName}
        layoutId={layoutId}
        layoutMetadata={layoutMetadataForWidgets}
        displayOrderForCreate={
          mergedLayoutPanel.length === 0
            ? 0
            : Math.max(
              ...mergedLayoutPanel.map((p) =>
                Number(p.kind === 'rl' ? p.rl.display_order : p.widget.display_order) || 0
              )
            ) + 1
        }
        editingWidget={editingSearchWidget}
        onSaved={async () => {
          setSuccess('Search widget saved');
          await fetchLayoutSearchWidgets();
          if (onSave) onSave();
        }}
      />
    </>
  );
};

export default RelatedListManager;
